import type Permissions from '@app/src/auth/permissions'
import type { ResourceCreator } from '@app/src/auth/User'
import type { Country, SolutionType } from '@app/src/types/resourceExplorer'
import { createContext, useContext } from 'react'

interface Item {
  id: string
  route: string
  href?: string
  icon?: string
  items?: Item[]
  legacy?: boolean
  target?: string
}

interface Image {
  url: string
}

interface Evaluations {
  status: string
  tier: number
  solutionType: string
}

interface NavigationOrganization {
  name: string
  country: Country
  vatNumber: string
  id: number
  image: Image
  evaluations?: Evaluations[]
  websiteAddress: string
  assignedSolutions?: AssignedSolution[]
}

export interface AssignedSolution {
  organizationId: number
  solutionType: SolutionType
}

export interface NavigationUser {
  name?: string
  familyName: string
  givenName: string
  organization?: NavigationOrganization
  image?: Image
  email: string
  items?: Item[][]
  culture: string
  id: number
  auth0UserId: string
  position: string
  phoneNumber: string
  createdAt: string
  hasAcceptedTAndC: boolean
}

export interface Account {
  user: NavigationUser
  organization?: NavigationOrganization
  items: Item[]
  referralCode?: string
  permissions: string[]
  isOwnerRestricted: boolean
}

export type AccountContextProps = {
  account?: Account
  isLoading: boolean
  fetchAccount: () => Promise<void>
  hasPermission: (permissionToVerify: Permissions | Permissions[], resource?: ResourceCreator) => boolean
}

const AccountContext = createContext<AccountContextProps>({
  account: undefined,
  isLoading: false,
  fetchAccount: async () => undefined,
  hasPermission: () => false,
})

export const useAccount = (): AccountContextProps => {
  return useContext(AccountContext)
}

export default AccountContext
